import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, Product } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useFavorites } from '../contexts/FavoritesContext';
import ProductCarousel from '../components/ProductCarousel';
import { getCategoryMeasurements } from '../utils/categoryMeasurements';
import { adminApi } from '../services/adminApi';
import { WEB_BASE_URL } from '../config/environment';
import './ProductDetail.css';

// Using the Product interface from api.ts which matches our database schema

// Helper function to get proper image URL
const getImageUrl = (imagePath: string | undefined): string => {
  if (!imagePath) {
    return `${WEB_BASE_URL}/products/IMG_2794.jpeg`;
  }
  
  // Bucket URLs (Tigris/Railway, Cloudflare R2) → route through our proxy; r2.dev is
  // rate-limited and blocked on some networks
  if (imagePath.includes('storageapi.dev') || imagePath.includes('railway.storage') || imagePath.includes('.r2.dev') || imagePath.includes('r2.cloudflarestorage.com')) {
    const key = imagePath.replace(/^https?:\/\/[^/]+\//, '');
    return `${WEB_BASE_URL}/api/upload/proxy/${key}`;
  }

  // Our proxy URL (new uploads) or other absolute URLs (Cloudinary etc.)
  if (imagePath.startsWith('https://') || imagePath.startsWith('http://') || imagePath.startsWith('/api/upload/proxy/')) {
    return imagePath;
  }
  
  // If it's a base64 image, use fallback
  if (imagePath.startsWith('data:image/')) {
    return `${WEB_BASE_URL}/products/IMG_2794.jpeg`;
  }
  
  // If it's a file path, use backend
  if (imagePath.startsWith('/products/')) {
    return `${WEB_BASE_URL}${imagePath}`;
  }
  
  // Fallback
  return `${WEB_BASE_URL}/products/IMG_2794.jpeg`;
};

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addToCart, state: cartState } = useCart();
  const { isFavorite: isProductFavorite, toggleFavorite } = useFavorites();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState(0);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'description' | 'details' | 'reviews'>('description');
  const [addingToCart, setAddingToCart] = useState(false);
  const [randomProducts, setRandomProducts] = useState<Product[]>([]);
  const [productMeasurements, setProductMeasurements] = useState<{ [key: string]: string }>({});

  // Load product data and check if it's favorited
  useEffect(() => {
    const loadProduct = async () => {
      if (!id) {
        setError('Product ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        console.log('🔍 Loading product:', id);
        
        // TRY API FIRST
        let productData;
        try {
          productData = await api.getProduct(parseInt(id));
          console.log('✅ Product loaded via API:', productData);
        } catch (apiError) {
          console.log('❌ API failed, trying direct backend call...');
          
          // FALLBACK: Direct backend call for mobile
          const directResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/products/${id}`);
          if (!directResponse.ok) {
            throw new Error('Product not found');
          }
          productData = await directResponse.json();
          console.log('✅ Product loaded via direct call:', productData);
        }
        
        setProduct(productData);
        
        // Auto-select the size if product has a size
        if (productData.size) {
          setSelectedSize(productData.size);
        }

        // Load random products for the carousel (excluding current product)
        try {
          const randomProductsData = await api.getRandomProducts(productData.id);
          setRandomProducts(randomProductsData);
        } catch (error) {
          console.error('Failed to load random products:', error);
        }

        // Load product measurements
        try {
          const measurements = await adminApi.getProductMeasurements(productData.id);
          setProductMeasurements(measurements);
        } catch (error) {
          console.error('Failed to load product measurements:', error);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id]);


  const handleAddToCart = async () => {
    if (!product) return;
    
    if (product.size && !selectedSize) {
      // Just return silently - user will notice they need to select size
      return;
    }

    try {
      setAddingToCart(true);
      // Always use quantity = 1 since each vintage item is unique
      await addToCart(product.id, 1, product.size ? selectedSize : undefined);
    } catch (error: any) {
      // Error handling is done in CartContext
      console.error('Failed to add to cart:', error);
    } finally {
      setAddingToCart(false);
    }
  };

  // Swipe left/right on the main photo to move between images (wraps around)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || !product || product.images.length < 2) return;

    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    // Ignore short or mostly vertical moves so page scrolling still works
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;

    const count = product.images.length;
    setSelectedImage(prev => (dx < 0 ? (prev + 1) % count : (prev - 1 + count) % count));
  };

  const addToWishlist = async () => {
    if (!product) return;

    try {
      await toggleFavorite(product.id);
    } catch (error) {
      console.error('Failed to update favorites:', error);
    }
  };


  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading product...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail-page">
        <div className="error-state">
          <h3>Error Loading Product</h3>
          <p>{error}</p>
          <Link to="/shop" className="back-to-shop-btn">
            ← Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="error-state">
          <h3>Product Not Found</h3>
          <p>The product you're looking for doesn't exist.</p>
          <Link to="/shop" className="back-to-shop-btn">
            ← Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="detail-container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/shop">Shop</Link>
          <span>/</span>
          {product.category && (
            <>
              <Link to={`/shop?category=${product.category.name}`}>{product.category.name}</Link>
              <span>/</span>
            </>
          )}
          <span>{product.name}</span>
        </nav>

        {/* Main Product Layout */}
        <div className="product-detail-grid">
          {/* Left Column - Image Gallery */}
          <div className="product-gallery">
            <div className="main-image-container">
              <div className="main-image" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                <img 
                  src={getImageUrl(product.images[selectedImage])} 
                  alt={product.name}
                  className="main-product-image"
                />
                <div className="image-badges">
                  {product.originalPrice && (
                    <span className="badge discount">
                      -{Math.round(((Number(product.originalPrice) - Number(product.price)) / Number(product.originalPrice)) * 100)}%
                    </span>
                  )}
                </div>
                <button 
                  className={`favorite-btn ${isProductFavorite(product.id) ? 'active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); addToWishlist(); }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill={isProductFavorite(product.id) ? "#ffffff" : "none"} xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9L17 13.74L18.18 20.74L12 17.77L5.82 20.74L7 13.74L2 9L8.91 8.26L12 2Z" stroke="#ffffff" strokeWidth="1.5"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="thumbnail-gallery">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img src={getImageUrl(image)} alt={`${product.name} ${index + 1}`} />
                </button>
              ))}
            </div>
          </div>

          {/* Right Column - Product Info */}
          <div className="detail-product-info">
            <div className="product-header">
              <div className="category-brand">
                {product.category && <span className="category">{product.category.name}</span>}
              </div>
            </div>

            <h1 className="detail-product-title">{product.name}</h1>
            
            {product.description && <p className="detail-product-desc">{product.description}</p>}

            {/* Size Selection */}
            {product.size && (
              <div className="detail-size-selection">
                <span className="size-label">Size: <span className="size-value">{product.size}</span></span>
                <p className="size-note">(Vintage sizing may vary - see measurements & fit details below ↓)</p>
              </div>
            )}

            <div className="price-container">
              <span className="detail-current-price">€{Number(product.price).toFixed(2)}</span>
              <span className="price-note">Tax included</span>
              {product.originalPrice && (
                <>
                  <span className="original-price">€{Number(product.originalPrice).toFixed(2)}</span>
                  <span className="savings">Save €{(Number(product.originalPrice) - Number(product.price)).toFixed(2)}</span>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button 
                className={`detail-add-to-cart-btn primary ${Array.isArray(cartState.items) && cartState.items.some(item => item.productId === product.id) ? 'added' : ''}`}
                onClick={handleAddToCart}
                disabled={!product.inStock || addingToCart}
              >
                {addingToCart ? 'Adding...' : 
                 product.isSold ? 'Sold' :
                 !product.inStock ? 'Out of Stock' :
                 Array.isArray(cartState.items) && cartState.items.some(item => item.productId === product.id) ? (
                   <>
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '6px'}}>
                       <polyline points="20,6 9,17 4,12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                     </svg>
                     In Cart
                   </>
                 ) : 'Add to Cart'}
              </button>
              <button 
                className={`detail-favorite-btn ${isProductFavorite(product.id) ? 'active' : ''}`}
                onClick={addToWishlist}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill={isProductFavorite(product.id) ? "#ffffff" : "none"} xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15.09 8.26L22 9L17 13.74L18.18 20.74L12 17.77L5.82 20.74L7 13.74L2 9L8.91 8.26L12 2Z" stroke="#ffffff" strokeWidth="1.5"/>
                </svg>
              </button>
              <span className="wishlist-label">Add to Wishlist</span>
            </div>

            {/* Product Features */}
            <div className="detail-product-features">
              <div className="detail-feature">
                <span className="detail-feature-icons">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 3H15V7H21L23 9V17H21C21 18.1046 20.1046 19 19 19C17.8954 19 17 18.1046 17 17H7C7 18.1046 6.10457 19 5 19C3.89543 19 3 18.1046 3 17H1V3Z" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="5" cy="17" r="2" stroke="#900e9b" strokeWidth="2"/>
                    <circle cx="19" cy="17" r="2" stroke="#900e9b" strokeWidth="2"/>
                  </svg>
                </span>
                <div className="detail-feature-text">
                  <strong>Free Shipping on orders over €70 (Greece)</strong>
                  <p>2-7 business days • <button className="shipping-policy-link" onClick={() => navigate('/shipping-policy')}>View shipping policy</button></p>
                </div>
              </div>
              <div className="detail-feature">
                <span className="detail-feature-icons">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <div className="detail-feature-text">
                  <strong>Authentic Vintage</strong>
                  <p>Hand-picked & verified</p>
                </div>
              </div>
              <div className="detail-feature">
                <span className="detail-feature-icons">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 10C21 17 12 23 12 23S3 17 3 10C3 6.13401 6.13401 3 10 3H14C17.866 3 21 6.13401 21 10Z" stroke="#900e9b" strokeWidth="2"/>
                    <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="#900e9b" strokeWidth="2"/>
                  </svg>
                </span>
                <div className="detail-feature-text">
                  <strong>In-Store Pickup Available</strong>
                  <p>Ready in 24-48 hours • <button className="store-info-link" onClick={() => navigate('/store-info')}>View store information</button></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Information */}
        <div className="product-info-section">
          <div className="info-grid">
            <div className="description-column">
              <h3>Description</h3>
              {product.description && <p className="item-description">{product.description}</p>}
              
              <div className="item-basics">
                <h4>Item Information</h4>
                <div className="info-row">
                  <span className="info-label">Size:</span>
                  <span className="info-value">{product.size || 'One Size'}</span>
                </div>
                {product.condition && (
                  <div className="info-row">
                    <span className="info-label">Condition:</span>
                    <span className="info-value">{product.condition}</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Availability:</span>
                  <span className="info-value">{product.isSold ? 'Sold' : product.inStock ? 'In Stock' : 'Out of Stock'}</span>
                </div>
              </div>
            </div>
            
            <div className="measurements-column">
              <h3>Size & Fit Guide</h3>
              
              {Object.keys(productMeasurements).length > 0 ? (
                <div className="measurement-group">
                  <h4>Actual Measurements</h4>
                  {(() => {
                    const categoryName = product.category?.name || '';
                    const productType = (product as any).productType;
                    const measurementFields = getCategoryMeasurements(categoryName, productType);
                    
                    return measurementFields.map(field => {
                      const value = productMeasurements[field.name];
                      if (value) {
                        return (
                          <div key={field.name} className="measurement-row">
                            <span>{field.label}</span>
                            <span>{value}</span>
                          </div>
                        );
                      }
                      return null;
                    });
                  })()}
                </div>
              ) : (
                <div className="measurement-group">
                  <h4>Actual Measurements</h4>
                  <p className="no-measurements">Measurements not available for this product</p>
                </div>
              )}
              
              {product.fitReference && (
                <div className="fit-info">
                  <h4>Fit Reference</h4>
                  <p className="fit-note">
                    Displayed on {product.fitReference.type === 'model' ? 'model' : 'dressform'}:
                  </p>
                  {product.fitReference.measurements.map(m => (
                    <div key={m.label} className="measurement-row">
                      <span>{m.label}</span>
                      <span>{m.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Related Products Carousel */}
        {randomProducts.length > 0 && (
          <ProductCarousel 
            products={randomProducts}
            title="You Might Also Like"
          />
        )}
      </div>
    </div>
  );
};

export default ProductDetail;